import React, { useState } from "react";
import PropTypes from "prop-types";
import { Print } from "./Modals/Print/Print";
import { X, Menu } from "lucide-react";
import { Search } from "./Modals/Search/Search";
import Delete from "./Modals/Delete/Delete";
import { Link, useNavigate } from "react-router-dom";

const ActionBar = ({
  title,
  fields,
  apiEndpoint,
  data,
  selectedRow,
  onRowSelect,
  userRole,
  onRefresh,
  onSearch,
  onFilter,
  dropdownData,
  rowKey = "id",
  handleSubmit,
  deleteMessage = "Are you sure you want to delete this item?",
  addLink = "/addIndent",
  editRoute,
  editButtonLabel = "Edit",
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const canManageItems = [
    "admin",
    "project engineer",
    "director",
    "location head",
  ].some((allowedRole) => userRole.toLowerCase() === allowedRole.toLowerCase());

  const canEditItems = ["purchase head"].some(
    (allowedRole) => userRole.toLowerCase() === allowedRole.toLowerCase()
  );

  // Modified to exclude project engineer from delete permissions
  const canDeleteItems = [
    "admin",
  ].some((allowedRole) => userRole.toLowerCase() === allowedRole.toLowerCase());

  const handleDeleteConfirmation = (id) => {
    setSelectedDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedDeleteId(null);
  };

  return (
    <div className="relative w-full bg-gray-100">
      {/* Mobile Header */}
      <div className="md:hidden flex justify-between items-center p-4 bg-blue-600 text-white">
        <h2 className="text-lg font-semibold">{title}</h2>
        <button
          aria-label="Toggle Mobile Menu"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-md hover:bg-blue-700 transition"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Action Buttons */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block absolute md:relative w-full bg-white shadow-lg md:shadow-none z-10`}
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4 bg-white border-b border-gray-200">
          <div className="flex items-center gap-4">
            <button
              className="px-4 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700 transition"
              onClick={() => navigate("/dashboard")}
            >
              Back
            </button>
            {canManageItems && (
              <Link
                to={addLink}
                className="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition"
              >
                Add
              </Link>
            )}
          </div>

          <div className="flex flex-wrap gap-3 justify-center">
            {!canEditItems && (
              <button
                onClick={() => {
                  if (selectedRow) {
                    navigate(editRoute, { state: { row: selectedRow } });
                  } else {
                    alert("Please select a row to edit!");
                  }
                }}
                className={`px-4 py-2 bg-yellow-600 text-white rounded-lg shadow hover:bg-yellow-700 transition ${
                  !selectedRow ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={!selectedRow}
              >
                {editButtonLabel}
              </button>
            )}
            {canDeleteItems && ( // Changed from canManageItems to canDeleteItems
              <button
                onClick={() => handleDeleteConfirmation(selectedRow[rowKey])}
                className={`px-4 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700 transition ${
                  !selectedRow ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={!selectedRow}
              >
                Delete
              </button>
            )}
          </div>

          <div className="relative z-20 flex gap-3 items-center">
            <Print data={data} />
            <Search onSearch={onSearch} onFilter={onFilter} />
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
              <p className="mb-6">{deleteMessage}</p>
              <div className="flex justify-center gap-4">
                <Delete
                  itemId={selectedDeleteId}
                  apiEndpoint={apiEndpoint}
                  onDelete={() => {
                    onRefresh();
                    handleCancelDelete();
                  }}
                />
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg shadow hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ActionBar.propTypes = {
  title: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  apiEndpoint: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  selectedRow: PropTypes.object,
  onRowSelect: PropTypes.func,
  userRole: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  onFilter: PropTypes.func,
  dropdownData: PropTypes.array,
  rowKey: PropTypes.string,
  handleSubmit: PropTypes.func,
  deleteMessage: PropTypes.string,
  addLink: PropTypes.string,
  editRoute: PropTypes.string,
  editButtonLabel: PropTypes.string,
};

export default ActionBar;